export const REMINDER_DURATION_MINUTES = 15;
export const REMINDER_REPEAT_COUNT = 30;
export const REMINDER_HOURS_IN_A_MONTH = 7.5;
export const CALENDAR_REMINDER_SESSION_THRESHOLD = 5;

export type ClockTime = {
  hours: number;
  minutes: number;
};

export type SlotId = 'morning' | 'lunch' | 'windDown' | 'custom';

export type CalendarProvider = 'google' | 'apple' | 'outlook';

export interface CalendarEvent {
  title: string;
  description: string;
  start: Date;
  end: Date;
  timeZone: string;
  repeatCount: number;
}

export const MORNING_TIME: ClockTime = { hours: 7, minutes: 30 };
export const LUNCH_TIME: ClockTime = { hours: 12, minutes: 30 };
export const CUSTOM_DEFAULT_TIME: ClockTime = { hours: 18, minutes: 0 };

const MINUTES_IN_A_DAY = 24 * 60;

export const roundToNearestHalfHour = (date: Date): ClockTime => {
  const totalMinutes = date.getHours() * 60 + date.getMinutes();
  const rounded = Math.round(totalMinutes / 30) * 30;
  const normalized = ((rounded % MINUTES_IN_A_DAY) + MINUTES_IN_A_DAY) % MINUTES_IN_A_DAY;

  return {
    hours: Math.floor(normalized / 60),
    minutes: normalized % 60,
  };
};

export const tomorrowAt = (time: ClockTime, now: Date): Date => {
  const start = new Date(now);
  start.setDate(start.getDate() + 1);
  start.setHours(time.hours, time.minutes, 0, 0);
  return start;
};

export const addMinutes = (date: Date, minutes: number): Date => {
  return new Date(date.getTime() + minutes * 60 * 1000);
};

export const browserTimeZone = (): string => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  } catch {
    return 'UTC';
  }
};

export const monthlyNewWords = (wordsToday: number, sessionsToday: number): number => {
  if (sessionsToday <= 0 || wordsToday <= 0) return 0;
  return Math.round((wordsToday / sessionsToday) * REMINDER_REPEAT_COUNT);
};

export const toTimeInputValue = (time: ClockTime): string => {
  const hours = String(time.hours).padStart(2, '0');
  const minutes = String(time.minutes).padStart(2, '0');
  return `${hours}:${minutes}`;
};

export const parseTimeInputValue = (value: string): ClockTime => {
  const match = value.match(/^(\d{2}):(\d{2})$/);
  if (!match) return CUSTOM_DEFAULT_TIME;

  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return CUSTOM_DEFAULT_TIME;

  return { hours, minutes };
};

export const buildCalendarEvent = (input: {
  title: string;
  description: string;
  time: ClockTime;
  now: Date;
  timeZone: string;
}): CalendarEvent => {
  const start = tomorrowAt(input.time, input.now);

  return {
    title: input.title,
    description: input.description,
    start,
    end: addMinutes(start, REMINDER_DURATION_MINUTES),
    timeZone: input.timeZone,
    repeatCount: REMINDER_REPEAT_COUNT,
  };
};
